import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { funnelStep, type FunnelProps } from './funnel';
import { routePattern, type RoutePattern } from './routePatterns';

/**
 * Page views in the builder, first-party (owner, 2026-10-03; landing_pages/docs/signup-at-build-handoff.md).
 *
 * The ads link straight to build.saddle-sites.com, so the landing site's Plausible never sees ad
 * traffic, and the funnel counted only named journey screens. Now every route is a `page_viewed`
 * and every exit a `page_left`, through the same ingest as the funnel (no consent needed: no
 * cookie, no identifier beyond the page load's session id, nothing typed).
 *
 * What a view carries, and nothing else:
 *  - `path`: the route PATTERN (`/project/:id`), never an id, a query or the idea;
 *  - `ref`: where the visit came from — ad | landing | app | direct | other;
 *  - `utm_campaign`, `utm_content`: the advertiser's own names for the campaign and the ad, as the
 *    link gave them, held to a safe shape in funnel.ts;
 *  - `has_fbclid`: whether the visit came from a Facebook click, never the click id itself.
 * The first page of the visit decides `ref` and the ad, kept for the tab in sessionStorage, so a
 * later page — and the steps beside it — can be tied to the ad that brought the visitor.
 *
 * Never sent to Meta: Meta counts its own PageView, and these carry the ad's names.
 */
const VISIT_KEY = 'saddle.visit';

interface Visit {
  ref: NonNullable<FunnelProps['ref']>;
  utm_campaign?: string;
  utm_content?: string;
  has_fbclid: boolean;
}

function referrerKind(params: URLSearchParams): Visit['ref'] {
  if (params.has('fbclid') || /^(meta|facebook|instagram|fb|ig)$/i.test(params.get('utm_source') ?? '')) return 'ad';
  let host = '';
  try {
    host = document.referrer ? new URL(document.referrer).hostname : '';
  } catch {
    host = '';
  }
  if (!host) return 'direct';
  if (host === 'saddle-sites.com' || host === 'www.saddle-sites.com') return 'landing';
  if (host === 'app.saddle-sites.com') return 'app';
  if (host === location.hostname) return 'direct';
  return 'other';
}

/** The visit's origin, read on its first page and kept for the tab. */
export function currentVisit(search: string = typeof location !== 'undefined' ? location.search : ''): Visit {
  try {
    const kept = sessionStorage.getItem(VISIT_KEY);
    if (kept) return JSON.parse(kept) as Visit;
  } catch {
    /* read fresh below */
  }
  const params = new URLSearchParams(search);
  const visit: Visit = {
    ref: referrerKind(params),
    has_fbclid: params.has('fbclid'),
    ...(params.get('utm_campaign') ? { utm_campaign: params.get('utm_campaign')! } : {}),
    ...(params.get('utm_content') ? { utm_content: params.get('utm_content')! } : {}),
  };
  try {
    sessionStorage.setItem(VISIT_KEY, JSON.stringify(visit));
  } catch {
    /* a private window: this page still counts, the next reads afresh */
  }
  return visit;
}

/** Time on a page, as the coarse bucket the event allows. */
export function secondsBucket(ms: number): NonNullable<FunnelProps['seconds']> {
  const s = ms / 1000;
  if (s < 3) return '<3';
  if (s < 10) return '3-10';
  if (s < 30) return '10-30';
  if (s < 120) return '30-120';
  return '120+';
}

/** Mounted once, inside the router: a view on every route change, a leave on the way out. */
export function usePageViews(): void {
  const { pathname, search } = useLocation();
  const current = useRef<{ path: RoutePattern; at: number } | null>(null);

  useEffect(() => {
    const path = routePattern(pathname);
    const leave = current.current;
    // A change of id on the same pattern (one project to another) is a new page too.
    if (leave) funnelStep('page_left', { path: leave.path, seconds: secondsBucket(Date.now() - leave.at) }, { pixel: false });
    const visit = currentVisit(search);
    funnelStep('page_viewed', { path, ...visit }, { pixel: false });
    current.current = { path, at: Date.now() };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    const onHide = (): void => {
      const leave = current.current;
      if (!leave) return;
      funnelStep('page_left', { path: leave.path, seconds: secondsBucket(Date.now() - leave.at) }, { now: true, pixel: false });
      // Coming back from the back-forward cache is a new view, counted from then.
      current.current = null;
    };
    const onShow = (e: PageTransitionEvent): void => {
      if (!e.persisted) return;
      const path = routePattern(window.location.pathname);
      funnelStep('page_viewed', { path, ...currentVisit() }, { pixel: false });
      current.current = { path, at: Date.now() };
    };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('pageshow', onShow);
    return () => {
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('pageshow', onShow);
    };
  }, []);
}
